import { cx } from 'cva'
import type { ReactNode } from 'react'

import { AppHeaderActions } from '#/routes/_app/-components/AppHeaderActions.js'
import { useMobileProfileSlot } from '#/routes/_app/-components/MobileProfileSlot.js'

/**
 * Shared app screen layout for title, actions, and page content.
 */
export function ScreenLayout(props: ScreenLayout.Props) {
  const { actions, children, description, heading, walletActions = true } = props
  const mobileProfile = useMobileProfileSlot()
  const hasHeaderContent = !!heading || !!actions || walletActions
  const showHeader = hasHeaderContent || !!mobileProfile
  const headerActions = (
    <>
      {actions}
      {walletActions && <AppHeaderActions />}
    </>
  )

  return (
    <div className="flex flex-col gap-8 pt-5 pb-8 max-md:pt-4">
      {showHeader && (
        <div className={cx('flex flex-col gap-1', !hasHeaderContent && 'md:hidden')}>
          <div className="flex flex-col gap-4 md:flex-row md:flex-wrap md:items-center md:justify-between md:gap-6">
            <div className="flex min-w-0 flex-1 items-start gap-4">
              <div className="flex min-w-0 flex-1 flex-col gap-1">{heading}</div>
              {mobileProfile && <div className="shrink-0 md:hidden">{mobileProfile}</div>}
            </div>
            {(actions || walletActions) && (
              <div className="flex items-center gap-2 max-sm:w-full [&>*]:max-sm:flex-1">
                {headerActions}
              </div>
            )}
          </div>
          {description}
        </div>
      )}
      {children}
    </div>
  )
}

export declare namespace ScreenLayout {
  /**
   * Props for the shared app screen layout.
   */
  type Props = {
    /** Right-aligned screen actions. */
    actions?: ReactNode | undefined
    /** Main screen content rendered below the header. */
    children: ReactNode
    /** Supporting content rendered below the aligned heading/action row. */
    description?: ReactNode | undefined
    /** Left-side title and summary content. */
    heading?: ReactNode | undefined
    /** Whether to show the shared copy-address and deposit actions. */
    walletActions?: boolean | undefined
  }
}
